import { FC } from 'react';
import { motion } from 'framer-motion';
import { cx } from '@emotion/css';
import { useIsClient } from 'usehooks-ts';
import Link from 'next/link';
import { useNavigation } from '../context/navigation-context-provider';
import { subMenuContainerAnimate, subMenuItemsAnimate } from '../helpers/animations';
import { NavigationItemProps } from '../types/navigation.types';
import styles from '../navigation.module.scss';
import { getRelativeUrl, isInternalLink } from '../helpers/utils';
import { cn } from '@phoenix/design-system/frontend/util/classNameUtils';

export const NavigationItem: FC<NavigationItemProps> = ({ item, isToplevelItem = true, index, isActiveRoute = false }) => {
  const { setState, state, config } = useNavigation();
  const isClient = useIsClient();

  if (item.type === 'Section') {
    return (
      <motion.li>
        <button
          type="button"
          className={cx(
            'relative flex h-full items-center justify-center px-12 stihl-text-interactive-text',
            'before:content-"" before:absolute before:left-0 before:block before:h-6 before:border-l before:border-secondary-mid',
            !config.name && index === 0 && 'pl-3 before:border-l-0',
            styles.mainNavSectionItem,
            isClient && isActiveRoute && styles.mainNavSectionItemActive
          )}
          onClick={() => {
            setState((prevState) => ({
              ...prevState,
              showBackdrop: true,
              showDesktopSubMenu: item.id,
            }));
          }}
        >
          <span>{item.name}</span>
        </button>
        {item.children && (
          <>
            <motion.div
              initial="exit"
              animate={state.showDesktopSubMenu === item.id ? 'enter' : 'exit'}
              variants={subMenuItemsAnimate}
              className="absolute left-0 right-0 top-[3.625rem] z-20 flex h-[3.625rem] bg-transparent"
            >
              <ul className="flex h-full gap-12 pl-3 stihl-text-body-default-reg stihl-page-container">
                {item.children?.map((subItem, navItemIndex) => (
                  <NavigationItem
                    key={subItem.id}
                    item={subItem}
                    isToplevelItem={false}
                    index={navItemIndex}
                    isActiveRoute={isActiveRoute}
                  />
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial="exit"
              animate={state.showDesktopSubMenu !== null ? 'enter' : 'exit'}
              variants={subMenuContainerAnimate}
              className="absolute left-0 right-0 top-[3.625rem] z-10 flex h-[3.625rem] bg-primary-white"
            />
          </>
        )}
      </motion.li>
    );
  }

  const commonProps = {
    ...(isClient && { 'data-active': isActiveRoute, 'aria-current': isActiveRoute }),
    className: cx(
      'relative flex h-full items-center justify-center',
      isToplevelItem
        ? 'before:content-"" px-12 stihl-text-interactive-text before:absolute before:left-0 before:block before:h-6 before:border-l before:border-secondary-mid'
        : 'first-child:pr-2 stihl-text-body-default-reg not-first:px-2',
      styles.mainNavSectionItem,
      isClient && isActiveRoute && styles.mainNavSectionItemActive
    ),
    'data-testid': `nav-item_${item.id}`,
    onClick: () => {
      item?.action?.();
      setState((s) => ({
        ...s,
        showDesktopSubMenu: null,
        showBackdrop: false,
      }));
    },
  };

  const commonLinkProps = {
    rel: item.target === '_blank' ? 'noopener noreferrer' : undefined,
    ...commonProps,
    className: cn(
      'focus-visible:outline-offset-[-3px] focus-visible:outline-[#252525] focus-visible:outline-[2px] focus-visible:outline',
      commonProps.className
    ),
  };

  return (
    <motion.li>
      {!item.url ? (
        <button type="button" {...commonProps}>
          <span>{item.name}</span>
        </button>
      ) : isInternalLink(item.url) ? (
        <Link locale={false} href={getRelativeUrl(item.url)} target={item.target} {...commonLinkProps}>
          <span>{item.name}</span>
        </Link>
      ) : (
        <a href={item.url} {...commonLinkProps}>
          <span>{item.name}</span>
        </a>
      )}
    </motion.li>
  );
};
